import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/auth.api.js';
import { AtomFitLogo, Spinner } from '../../components/design/AtomFitUI.js';
import { translate } from '../../i18n/index.js';
import { verificationLoginUrl } from '../../utils/verification-login.js';

export function EmailVerification() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const redirect = params.get('redirect');
  const safeRedirect =
    redirect?.startsWith('/') && !redirect.startsWith('//') && !redirect.includes('\\')
      ? redirect
      : null;
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const loginUrl = verificationLoginUrl(verifiedEmail, safeRedirect);
  const [status, setStatus] = useState<'ready' | 'loading' | 'success' | 'invalid' | 'error'>(
    token ? 'ready' : 'invalid',
  );

  async function confirm() {
    if (!token || status === 'loading' || status === 'success') return;
    setStatus('loading');
    try {
      const { data } = await authApi.confirmEmailVerification(token);
      setVerifiedEmail(data.email);
      setStatus('success');
      // Remove the secret from the address bar after successful consumption.
      window.history.replaceState(
        null,
        '',
        `/auth/verify-email${safeRedirect ? `?redirect=${encodeURIComponent(safeRedirect)}` : ''}`,
      );
    } catch (err: unknown) {
      const code = (err as { response?: { status?: number } }).response?.status;
      setStatus(code === 401 ? 'invalid' : 'error');
    }
  }

  return (
    <main
      className="af-screen"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
      }}
    >
      <section style={{ width: 'min(420px, 100%)' }}>
        <Link
          className="af-logo"
          to="/"
          style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}
        >
          <AtomFitLogo />
        </Link>
        <div
          className="af-card"
          style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 20 }}
        >
          <h1 style={{ fontSize: 22, margin: 0 }}>{translate('email_verification.title')}</h1>
          <p role="status" style={{ margin: 0, color: 'var(--af-muted)' }}>
            {translate(
              `email_verification.${status === 'success' ? 'success' : status === 'invalid' ? 'invalid' : 'description'}`,
            )}
          </p>
          {status === 'error' && (
            <div className="af-error" role="alert">
              {translate('email_verification.confirm_error')}
            </div>
          )}
          {status !== 'success' && status !== 'invalid' && (
            <button
              className="af-primary-btn"
              disabled={status === 'loading'}
              onClick={() => void confirm()}
            >
              {status === 'loading' ? (
                <>
                  <Spinner /> {translate('email_verification.working')}
                </>
              ) : (
                translate('email_verification.confirm')
              )}
            </button>
          )}
          <Link className={status === 'success' ? 'af-primary-btn' : undefined} to={loginUrl}>
            {translate('email_verification.login')}
          </Link>
        </div>
      </section>
    </main>
  );
}
